import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Star, Check, Lock, ChevronRight, RotateCcw,
  Play, Info, Cog, Weight, ArrowDown, Triangle, Settings2
} from 'lucide-react';

const MISSIONS = [
  {
    id: 1, title: 'The Lever', difficulty: 1, type: 'lever',
    description: 'Balance the lever by adjusting the weight position. Heavier weights need to be closer to the fulcrum!',
    fact: 'Archimedes said "Give me a lever long enough and I shall move the world!" The lever law: F₁×d₁ = F₂×d₂.',
  },
  {
    id: 2, title: 'Pulley System', difficulty: 1, type: 'pulley',
    description: 'Add pulleys to reduce the force needed to lift the heavy box. More pulleys = less effort!',
    fact: 'Each additional pulley halves the effort needed but doubles the rope length to pull. A 4-pulley system needs only ¼ the force!',
  },
  {
    id: 3, title: 'Inclined Plane', difficulty: 1, type: 'incline',
    description: 'Adjust the ramp angle. A gentler slope means less force needed but more distance to push!',
    fact: 'Pyramids were built using ramps! Force = Weight × sin(angle). A 30° ramp cuts the force in half.',
  },
  {
    id: 4, title: 'Wheel & Axle', difficulty: 2, type: 'wheel',
    description: 'Adjust the wheel and axle radii. A bigger wheel gives more mechanical advantage!',
    fact: 'A steering wheel is a wheel & axle. The bigger the wheel, the more torque it delivers. MA = R_wheel / R_axle.',
  },
  {
    id: 5, title: 'Gear Train', difficulty: 2, type: 'gears',
    description: 'Connect gears of different sizes. A small driver gear + large driven gear gives more torque!',
    fact: 'In a bicycle, you change gears to trade speed for torque (uphill) or torque for speed (downhill). Gear ratio = teeth_driven / teeth_driver.',
  },
  {
    id: 6, title: 'Bridge Builder', difficulty: 3, type: 'bridge',
    description: 'Choose beam materials and supports to hold the maximum load. Triangles are strongest!',
    fact: 'Triangles are the strongest shape because they distribute force evenly. The Eiffel Tower uses 18,000 iron trusses in triangular patterns!',
  },
  {
    id: 7, title: 'Screw Mechanism', difficulty: 2, type: 'screw',
    description: 'Adjust thread pitch — finer threads need more turns but less force per turn!',
    fact: 'A screw is a wrapped inclined plane! MA = circumference / pitch. Finer threads hold tighter, coarser threads drive faster.',
  },
  {
    id: 8, title: 'Wedge Splitter', difficulty: 1, type: 'wedge',
    description: 'Adjust the wedge angle. A thinner wedge needs less force but splits slower!',
    fact: 'Axes, knives, and chisels are wedges. A thin blade cuts easier because force concentrates on a smaller area. Pressure = Force / Area.',
  },
  {
    id: 9, title: 'Hydraulic Press', difficulty: 3, type: 'hydraulic',
    description: 'Adjust piston sizes. A small input piston + large output piston multiplies force!',
    fact: "Pascal's Principle: Pressure applied anywhere in a fluid is transmitted equally. Car brakes, excavators, and barber chairs use hydraulics!",
  },
  {
    id: 10, title: 'Rube Goldberg', difficulty: 3, type: 'rube',
    description: 'Combine all machines! Set the chain reaction in motion from start to finish!',
    fact: 'Rube Goldberg machines use complex chains of simple machines to do simple tasks. Engineers optimize for efficiency — Rube Goldberg celebrates the opposite!',
  },
];

export default function EngineeringSimLab() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [showMissions, setShowMissions] = useState(true);
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('engineering-sim', 10);
  const [score, setScore] = useState(0);
  const [showFact, setShowFact] = useState(false);

  // Machine-specific states
  const [leverPos, setLeverPos] = useState(50); // 0-100 position of weight
  const [leverWeight1] = useState(10);
  const [leverWeight2, setLeverWeight2] = useState(5);
  const [pulleyCount, setPulleyCount] = useState(1);
  const [rampAngle, setRampAngle] = useState(45);
  const [wheelRadius, setWheelRadius] = useState(3);
  const [axleRadius, setAxleRadius] = useState(1);
  const [driverTeeth, setDriverTeeth] = useState(12);
  const [drivenTeeth, setDrivenTeeth] = useState(24);
  const [bridgeType, setBridgeType] = useState('beam');
  const [bridgeLoad, setBridgeLoad] = useState(0);
  const [screwPitch, setScrewPitch] = useState(5);
  const [wedgeAngle, setWedgeAngle] = useState(30);
  const [smallPiston, setSmallPiston] = useState(2);
  const [largePiston, setLargePiston] = useState(8);
  const [rubeStep, setRubeStep] = useState(0);
  const [simRunning, setSimRunning] = useState(false);
  const animRef = useRef(null);

  const mission = MISSIONS[currentMission];

  const resetState = () => {
    setLeverPos(50); setLeverWeight2(5);
    setPulleyCount(1); setRampAngle(45);
    setWheelRadius(3); setAxleRadius(1);
    setDriverTeeth(12); setDrivenTeeth(24);
    setBridgeType('beam'); setBridgeLoad(0);
    setScrewPitch(5); setWedgeAngle(30);
    setSmallPiston(2); setLargePiston(8);
    setRubeStep(0); setSimRunning(false);
    setShowFact(false);
    if (animRef.current) cancelAnimationFrame(animRef.current);
  };

  useEffect(() => { resetState(); }, [currentMission]);

  // Canvas rendering
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, '#0d0d1a');
    grad.addColorStop(1, '#0a0a14');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    if (mission.type === 'lever') {
      // Fulcrum triangle
      const fx = w / 2, fy = h * 0.7;
      ctx.fillStyle = '#475569';
      ctx.beginPath(); ctx.moveTo(fx - 20, fy + 20); ctx.lineTo(fx + 20, fy + 20); ctx.lineTo(fx, fy - 5); ctx.closePath(); ctx.fill();
      // Lever beam
      const tilt = ((leverPos - 50) / 50) * 15 * (Math.PI / 180);
      ctx.save(); ctx.translate(fx, fy - 5);
      ctx.rotate(tilt);
      ctx.fillStyle = '#64748b'; ctx.fillRect(-180, -4, 360, 8); ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 1; ctx.strokeRect(-180, -4, 360, 8);
      // Weights
      const w1x = -150, w2x = 150 * (leverPos / 50 - 1);
      ctx.fillStyle = '#ef4444'; ctx.fillRect(w1x - 15, -20, 30, 16); ctx.fillStyle = '#fff'; ctx.font = '10px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(`${leverWeight1}kg`, w1x, -8);
      ctx.fillStyle = '#3b82f6'; ctx.fillRect(w2x - 15, -20, 30, 16); ctx.fillStyle = '#fff'; ctx.fillText(`${leverWeight2}kg`, w2x, -8);
      ctx.restore();
      // Balance indicator
      const torque1 = leverWeight1 * 150;
      const torque2 = leverWeight2 * Math.abs(150 * (leverPos / 50 - 1));
      const balanced = Math.abs(torque1 - torque2) < torque1 * 0.15;
      ctx.fillStyle = balanced ? '#10b981' : '#f59e0b';
      ctx.font = '14px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(balanced ? 'BALANCED!' : 'Not balanced...', w / 2, h * 0.9);
    } else if (mission.type === 'pulley') {
      const baseY = 50, boxY = h - 80;
      // Support bar
      ctx.fillStyle = '#475569'; ctx.fillRect(50, baseY - 5, w - 100, 10);
      // Pulleys
      for (let i = 0; i < pulleyCount; i++) {
        const px = 150 + i * 80;
        ctx.strokeStyle = '#8b5cf6'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(px, baseY + 25, 15, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = '#8b5cf6'; ctx.beginPath(); ctx.arc(px, baseY + 25, 4, 0, Math.PI * 2); ctx.fill();
        // Rope
        ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(px, baseY + 40); ctx.lineTo(px, boxY - 20); ctx.stroke();
      }
      // Box
      ctx.fillStyle = '#1e293b'; ctx.strokeStyle = '#475569'; ctx.lineWidth = 2;
      ctx.fillRect(130, boxY - 20, 80, 40); ctx.strokeRect(130, boxY - 20, 80, 40);
      ctx.fillStyle = '#fff'; ctx.font = '12px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('100 kg', 170, boxY + 5);
      // Force label
      const force = (100 * 9.8 / pulleyCount).toFixed(0);
      ctx.fillStyle = '#10b981'; ctx.font = '14px sans-serif';
      ctx.fillText(`Effort: ${force} N`, w / 2, h - 20);
      ctx.fillStyle = '#f59e0b'; ctx.font = '11px sans-serif';
      ctx.fillText(`MA = ${pulleyCount}x`, w / 2, h - 40);
    } else if (mission.type === 'incline') {
      const angleRad = rampAngle * Math.PI / 180;
      const rampLen = 300;
      const rampH = rampLen * Math.sin(angleRad);
      const rampW = rampLen * Math.cos(angleRad);
      const bx = w * 0.15, by = h * 0.8;
      // Ramp
      ctx.fillStyle = '#334155'; ctx.beginPath();
      ctx.moveTo(bx, by); ctx.lineTo(bx + rampW, by); ctx.lineTo(bx, by - rampH); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = '#64748b'; ctx.lineWidth = 2; ctx.beginPath();
      ctx.moveTo(bx, by); ctx.lineTo(bx + rampW, by); ctx.lineTo(bx, by - rampH); ctx.closePath(); ctx.stroke();
      // Box on ramp
      const boxProgress = 0.5;
      const bxPos = bx + rampW * boxProgress * Math.cos(angleRad);
      const byPos = by - rampW * boxProgress * Math.sin(angleRad);
      ctx.save(); ctx.translate(bxPos, byPos - 15); ctx.rotate(-angleRad);
      ctx.fillStyle = '#3b82f6'; ctx.fillRect(-15, -15, 30, 30);
      ctx.fillStyle = '#fff'; ctx.font = '10px sans-serif'; ctx.textAlign = 'center'; ctx.fillText('50kg', 0, 3);
      ctx.restore();
      // Info
      const force = (50 * 9.8 * Math.sin(angleRad)).toFixed(0);
      ctx.fillStyle = '#10b981'; ctx.font = '14px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`Force along ramp: ${force} N`, w / 2, h - 20);
      ctx.fillStyle = '#f59e0b'; ctx.font = '11px sans-serif';
      ctx.fillText(`Angle: ${rampAngle}° | Without ramp: 490 N`, w / 2, h - 40);
    } else if (mission.type === 'wheel') {
      const cx = w / 2, cy = h / 2;
      // Outer wheel
      ctx.strokeStyle = '#8b5cf6'; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.arc(cx, cy, wheelRadius * 25, 0, Math.PI * 2); ctx.stroke();
      // Axle
      ctx.fillStyle = '#475569';
      ctx.beginPath(); ctx.arc(cx, cy, axleRadius * 25, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#64748b'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(cx, cy, axleRadius * 25, 0, Math.PI * 2); ctx.stroke();
      // Spokes
      ctx.strokeStyle = '#64748b40'; ctx.lineWidth = 1;
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * axleRadius * 25, cy + Math.sin(a) * axleRadius * 25);
        ctx.lineTo(cx + Math.cos(a) * wheelRadius * 25, cy + Math.sin(a) * wheelRadius * 25);
        ctx.stroke();
      }
      const ma = (wheelRadius / axleRadius).toFixed(1);
      ctx.fillStyle = '#10b981'; ctx.font = '14px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`MA = ${ma}x`, w / 2, h - 20);
    } else if (mission.type === 'gears') {
      const cx1 = w * 0.35, cx2 = w * 0.65, cy = h / 2;
      const r1 = driverTeeth * 2, r2 = drivenTeeth * 2;
      // Driver gear
      ctx.strokeStyle = '#f43f5e'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(cx1, cy, r1, 0, Math.PI * 2); ctx.stroke();
      for (let i = 0; i < driverTeeth; i++) {
        const a = (i / driverTeeth) * Math.PI * 2;
        ctx.beginPath(); ctx.moveTo(cx1 + Math.cos(a) * r1, cy + Math.sin(a) * r1);
        ctx.lineTo(cx1 + Math.cos(a) * (r1 + 8), cy + Math.sin(a) * (r1 + 8)); ctx.stroke();
      }
      ctx.fillStyle = '#fff'; ctx.font = '12px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`${driverTeeth}T`, cx1, cy + 4);
      // Driven gear
      ctx.strokeStyle = '#3b82f6'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(cx2, cy, r2, 0, Math.PI * 2); ctx.stroke();
      for (let i = 0; i < drivenTeeth; i++) {
        const a = (i / drivenTeeth) * Math.PI * 2;
        ctx.beginPath(); ctx.moveTo(cx2 + Math.cos(a) * r2, cy + Math.sin(a) * r2);
        ctx.lineTo(cx2 + Math.cos(a) * (r2 + 8), cy + Math.sin(a) * (r2 + 8)); ctx.stroke();
      }
      ctx.fillStyle = '#fff'; ctx.fillText(`${drivenTeeth}T`, cx2, cy + 4);
      const ratio = (drivenTeeth / driverTeeth).toFixed(1);
      ctx.fillStyle = '#10b981'; ctx.font = '14px sans-serif';
      ctx.fillText(`Gear Ratio: ${ratio}:1  |  Torque: ${ratio}x  |  Speed: ${(1 / ratio).toFixed(1)}x`, w / 2, h - 20);
    } else if (mission.type === 'bridge') {
      const by = h * 0.6;
      // Supports
      ctx.fillStyle = '#475569';
      ctx.fillRect(80, by, 15, 80); ctx.fillRect(w - 95, by, 15, 80);
      // Bridge deck
      ctx.fillStyle = bridgeType === 'truss' ? '#10b981' : bridgeType === 'arch' ? '#3b82f6' : '#64748b';
      ctx.fillRect(80, by - 10, w - 160, 10);
      if (bridgeType === 'truss') {
        ctx.strokeStyle = '#10b98180'; ctx.lineWidth = 2;
        for (let i = 0; i < 6; i++) {
          const x = 95 + i * ((w - 190) / 6);
          ctx.beginPath(); ctx.moveTo(x, by - 10); ctx.lineTo(x + (w - 190) / 12, by - 50); ctx.lineTo(x + (w - 190) / 6, by - 10); ctx.stroke();
        }
      }
      if (bridgeType === 'arch') {
        ctx.strokeStyle = '#3b82f680'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(w / 2, by + 40, (w - 180) / 2, Math.PI, 0); ctx.stroke();
      }
      // Load indicator
      const maxLoad = bridgeType === 'truss' ? 100 : bridgeType === 'arch' ? 80 : 40;
      const loadRatio = bridgeLoad / maxLoad;
      ctx.fillStyle = loadRatio > 0.9 ? '#ef4444' : loadRatio > 0.6 ? '#f59e0b' : '#10b981';
      ctx.fillRect(w / 2 - 15, by - 10 - bridgeLoad * 0.3, 30, bridgeLoad * 0.3);
      ctx.fillStyle = '#fff'; ctx.font = '11px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`${bridgeLoad} tons`, w / 2, by - 15 - bridgeLoad * 0.3);
      ctx.fillStyle = '#f59e0b'; ctx.font = '12px sans-serif';
      ctx.fillText(`Max capacity: ${maxLoad} tons | Type: ${bridgeType.toUpperCase()}`, w / 2, h - 20);
    } else if (mission.type === 'screw') {
      const cx = w / 2, cy = h * 0.4;
      ctx.strokeStyle = '#8b5cf6'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(cx - 20, 60); ctx.lineTo(cx + 20, 60); ctx.lineTo(cx + 15, h * 0.75); ctx.lineTo(cx - 15, h * 0.75); ctx.closePath(); ctx.stroke();
      // Threads
      const threadCount = Math.round(200 / screwPitch);
      ctx.strokeStyle = '#a78bfa80';
      for (let i = 0; i < threadCount; i++) {
        const y = 65 + i * screwPitch;
        if (y > h * 0.73) break;
        const ww = 15 + (y - 60) * 0.02;
        ctx.beginPath(); ctx.moveTo(cx - ww - 5, y); ctx.lineTo(cx + ww + 5, y); ctx.stroke();
      }
      const ma = (2 * Math.PI * 15 / screwPitch).toFixed(1);
      ctx.fillStyle = '#10b981'; ctx.font = '14px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`MA = ${ma}x  |  Pitch: ${screwPitch}mm`, w / 2, h - 20);
    } else if (mission.type === 'wedge') {
      const cx = w / 2, tip = h * 0.25;
      const halfW = Math.tan((wedgeAngle / 2) * Math.PI / 180) * 200;
      ctx.fillStyle = '#64748b'; ctx.beginPath();
      ctx.moveTo(cx, tip); ctx.lineTo(cx - halfW, tip + 200); ctx.lineTo(cx + halfW, tip + 200); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 2; ctx.beginPath();
      ctx.moveTo(cx, tip); ctx.lineTo(cx - halfW, tip + 200); ctx.lineTo(cx + halfW, tip + 200); ctx.closePath(); ctx.stroke();
      const ma = (1 / Math.sin((wedgeAngle / 2) * Math.PI / 180)).toFixed(1);
      ctx.fillStyle = '#10b981'; ctx.font = '14px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`MA = ${ma}x  |  Angle: ${wedgeAngle}°`, w / 2, h - 20);
    } else if (mission.type === 'hydraulic') {
      const leftX = w * 0.25, rightX = w * 0.7, by = h * 0.7;
      // Small piston
      ctx.fillStyle = '#3b82f6'; ctx.fillRect(leftX - smallPiston * 8, by - 80, smallPiston * 16, 80);
      ctx.strokeStyle = '#60a5fa'; ctx.lineWidth = 2; ctx.strokeRect(leftX - smallPiston * 8, by - 80, smallPiston * 16, 80);
      // Large piston
      ctx.fillStyle = '#8b5cf6'; ctx.fillRect(rightX - largePiston * 8, by - 80, largePiston * 16, 80);
      ctx.strokeStyle = '#a78bfa'; ctx.lineWidth = 2; ctx.strokeRect(rightX - largePiston * 8, by - 80, largePiston * 16, 80);
      // Fluid
      ctx.fillStyle = '#06b6d430'; ctx.fillRect(leftX - smallPiston * 8, by, smallPiston * 16, 30);
      ctx.fillRect(rightX - largePiston * 8, by, largePiston * 16, 30);
      ctx.fillRect(leftX + smallPiston * 8, by, rightX - largePiston * 8 - leftX - smallPiston * 8, 30);
      // Labels
      ctx.fillStyle = '#fff'; ctx.font = '11px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`r=${smallPiston}`, leftX, by + 50);
      ctx.fillText(`R=${largePiston}`, rightX, by + 50);
      const ma = ((largePiston * largePiston) / (smallPiston * smallPiston)).toFixed(1);
      ctx.fillStyle = '#10b981'; ctx.font = '14px sans-serif';
      ctx.fillText(`Force Multiplier: ${ma}x`, w / 2, h - 20);
    } else if (mission.type === 'rube') {
      const stages = ['Lever', 'Ball rolls', 'Hits Pulley', 'Lifts Weight', 'Drops on Gear', 'Gear Spins', 'Push Wedge', 'Splits Block', 'Hydraulic Push', 'DONE!'];
      stages.forEach((s, i) => {
        const x = 30 + (i % 5) * ((w - 60) / 5);
        const y = i < 5 ? 80 : 220;
        ctx.fillStyle = i <= rubeStep ? '#10b981' : '#1e293b';
        ctx.strokeStyle = i <= rubeStep ? '#10b981' : '#475569';
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.roundRect(x, y, (w - 80) / 5, 60, 8); ctx.fill(); ctx.stroke();
        ctx.fillStyle = i <= rubeStep ? '#fff' : '#64748b';
        ctx.font = '10px sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(s, x + (w - 80) / 10, y + 35);
        ctx.fillText(`Step ${i + 1}`, x + (w - 80) / 10, y + 20);
      });
      if (simRunning && rubeStep < 9) {
        ctx.fillStyle = '#f59e0b'; ctx.font = '16px sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Chain reaction in progress...', w / 2, h - 30);
      }
    }
  }, [mission, leverPos, leverWeight1, leverWeight2, pulleyCount, rampAngle, wheelRadius, axleRadius, driverTeeth, drivenTeeth, bridgeType, bridgeLoad, screwPitch, wedgeAngle, smallPiston, largePiston, rubeStep, simRunning]);

  useEffect(() => { renderCanvas(); }, [renderCanvas]);

  // Rube Goldberg animation
  useEffect(() => {
    if (mission.type === 'rube' && simRunning && rubeStep < 9) {
      const timer = setTimeout(() => setRubeStep(s => s + 1), 800);
      return () => clearTimeout(timer);
    }
  }, [mission.type, simRunning, rubeStep]);

  const checkGoal = () => {
    switch (mission.type) {
      case 'lever': return Math.abs(leverWeight1 * 150 - leverWeight2 * Math.abs(150 * (leverPos / 50 - 1))) < leverWeight1 * 150 * 0.15;
      case 'pulley': return pulleyCount >= 3;
      case 'incline': return rampAngle <= 20;
      case 'wheel': return wheelRadius / axleRadius >= 3;
      case 'gears': return drivenTeeth / driverTeeth >= 2;
      case 'bridge': return bridgeType === 'truss' && bridgeLoad >= 80;
      case 'screw': return screwPitch <= 2;
      case 'wedge': return wedgeAngle <= 15;
      case 'hydraulic': return (largePiston * largePiston) / (smallPiston * smallPiston) >= 10;
      case 'rube': return rubeStep >= 9;
      default: return false;
    }
  };

  const completeMission = () => {
    if (!completedMissions.includes(currentMission)) {
      saveProgress(currentMission);
      setScore(s => s + mission.difficulty * 10);
    }
    setShowFact(true);
  };

  const goalMet = checkGoal();
  const isCompleted = completedMissions.includes(currentMission);

  /* ─── Mission Selector ─── */
  if (showMissions) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/labs')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="text-lg font-bold flex items-center gap-2"><Settings2 className="w-5 h-5 text-orange-400" /> Engineering Simulator</h1>
            <Badge variant="secondary" className="bg-orange-500/20 text-orange-300">{score} pts</Badge>
          </div>
        </header>
        <main className="max-w-6xl mx-auto p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1">Choose a Machine</h2>
            <p className="text-sm text-muted-foreground">Build, adjust, and test simple & compound machines!</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="mission-grid">
            {MISSIONS.map((m, idx) => {
              const locked = !unlockedMissions.includes(idx);
              const done = completedMissions.includes(idx);
              return (
                <motion.button key={m.id} whileHover={!locked ? { scale: 1.04 } : {}} whileTap={!locked ? { scale: 0.97 } : {}}
                  onClick={() => { if (!locked) { setCurrentMission(idx); setShowMissions(false); } }}
                  disabled={locked}
                  className={`relative p-4 rounded-2xl border text-left transition-all ${
                    done ? 'border-emerald-500/30 bg-emerald-500/5' :
                    locked ? 'border-border bg-card opacity-70 cursor-not-allowed' :
                    'border-border bg-card hover:border-orange-500/30 hover:bg-orange-500/5'
                  }`}
                  data-testid={`mission-${idx}`}>
                  {locked && <Lock className="absolute top-3 right-3 w-3 h-3 text-muted-foreground/30" />}
                  {done && <Check className="absolute top-3 right-3 w-4 h-4 text-emerald-400" />}
                  <span className="text-[10px] text-foreground uppercase tracking-widest">Mission {idx + 1}</span>
                  <p className="text-sm font-semibold mt-1 text-foreground leading-tight">{m.title}</p>
                  <div className="flex items-center gap-0.5 mt-2">
                    {[...Array(3)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < m.difficulty ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/20'}`} />)}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  /* ─── Lab View ─── */
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => setShowMissions(true)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-to-missions">
            <ArrowLeft className="w-4 h-4" /> Missions
          </button>
          <h1 className="text-sm font-semibold text-foreground/80">{mission.title}</h1>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground/70">{score} pts</span>
            <span className="text-xs text-muted-foreground/70">{completedMissions.length}/10</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4" data-testid="engineering-bench">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-4">
            {/* Canvas */}
            <div className="rounded-2xl border border-border bg-card p-2" data-testid="sim-area">
              <canvas ref={canvasRef} width={600} height={360} className="w-full rounded-xl bg-[#0d0d14]" data-testid="eng-canvas" />
            </div>

            {/* Controls */}
            <div className="p-4 rounded-2xl border border-border bg-card space-y-3" data-testid="controls-panel">
              {mission.type === 'lever' && (<>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Weight 2 Position</span><span className="text-foreground font-mono">{leverPos}%</span></label>
                  <input type="range" min={10} max={90} value={leverPos} onChange={e => setLeverPos(+e.target.value)} className="w-full accent-orange-500" data-testid="slider-lever-pos" /></div>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Weight 2 Mass</span><span className="text-foreground font-mono">{leverWeight2}kg</span></label>
                  <input type="range" min={1} max={20} value={leverWeight2} onChange={e => setLeverWeight2(+e.target.value)} className="w-full accent-orange-500" data-testid="slider-lever-w2" /></div>
              </>)}
              {mission.type === 'pulley' && (
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Number of Pulleys</span><span className="text-foreground font-mono">{pulleyCount}</span></label>
                  <input type="range" min={1} max={6} value={pulleyCount} onChange={e => setPulleyCount(+e.target.value)} className="w-full accent-orange-500" data-testid="slider-pulleys" /></div>
              )}
              {mission.type === 'incline' && (
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Ramp Angle</span><span className="text-foreground font-mono">{rampAngle}°</span></label>
                  <input type="range" min={5} max={80} value={rampAngle} onChange={e => setRampAngle(+e.target.value)} className="w-full accent-orange-500" data-testid="slider-angle" /></div>
              )}
              {mission.type === 'wheel' && (<>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Wheel Radius</span><span className="text-foreground font-mono">{wheelRadius}</span></label>
                  <input type="range" min={2} max={6} value={wheelRadius} onChange={e => setWheelRadius(+e.target.value)} className="w-full accent-orange-500" data-testid="slider-wheel" /></div>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Axle Radius</span><span className="text-foreground font-mono">{axleRadius}</span></label>
                  <input type="range" min={1} max={4} value={axleRadius} onChange={e => setAxleRadius(+e.target.value)} className="w-full accent-orange-500" data-testid="slider-axle" /></div>
              </>)}
              {mission.type === 'gears' && (<>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Driver Teeth</span><span className="text-foreground font-mono">{driverTeeth}</span></label>
                  <input type="range" min={8} max={30} value={driverTeeth} onChange={e => setDriverTeeth(+e.target.value)} className="w-full accent-rose-500" data-testid="slider-driver" /></div>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Driven Teeth</span><span className="text-foreground font-mono">{drivenTeeth}</span></label>
                  <input type="range" min={8} max={48} value={drivenTeeth} onChange={e => setDrivenTeeth(+e.target.value)} className="w-full accent-blue-500" data-testid="slider-driven" /></div>
              </>)}
              {mission.type === 'bridge' && (<>
                <div className="flex gap-2">
                  {['beam','arch','truss'].map(t => (
                    <button key={t} onClick={() => { setBridgeType(t); setBridgeLoad(0); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${bridgeType === t ? 'bg-orange-500 text-white' : 'bg-muted/50 text-muted-foreground hover:bg-muted'}`}
                      data-testid={`bridge-${t}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
                  ))}
                </div>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Load</span><span className="text-foreground font-mono">{bridgeLoad} tons</span></label>
                  <input type="range" min={0} max={120} value={bridgeLoad} onChange={e => setBridgeLoad(+e.target.value)} className="w-full accent-orange-500" data-testid="slider-load" /></div>
              </>)}
              {mission.type === 'screw' && (
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Thread Pitch (mm)</span><span className="text-foreground font-mono">{screwPitch}mm</span></label>
                  <input type="range" min={1} max={10} value={screwPitch} onChange={e => setScrewPitch(+e.target.value)} className="w-full accent-orange-500" data-testid="slider-pitch" /></div>
              )}
              {mission.type === 'wedge' && (
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Wedge Angle</span><span className="text-foreground font-mono">{wedgeAngle}°</span></label>
                  <input type="range" min={5} max={60} value={wedgeAngle} onChange={e => setWedgeAngle(+e.target.value)} className="w-full accent-orange-500" data-testid="slider-wedge" /></div>
              )}
              {mission.type === 'hydraulic' && (<>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Small Piston Radius</span><span className="text-foreground font-mono">{smallPiston}</span></label>
                  <input type="range" min={1} max={5} value={smallPiston} onChange={e => setSmallPiston(+e.target.value)} className="w-full accent-blue-500" data-testid="slider-small" /></div>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Large Piston Radius</span><span className="text-foreground font-mono">{largePiston}</span></label>
                  <input type="range" min={3} max={12} value={largePiston} onChange={e => setLargePiston(+e.target.value)} className="w-full accent-violet-500" data-testid="slider-large" /></div>
              </>)}
              {mission.type === 'rube' && (
                <Button onClick={() => setSimRunning(true)} disabled={simRunning} className="bg-gradient-to-r from-orange-500 to-red-600" data-testid="start-chain">
                  <Play className="w-4 h-4 mr-1" /> Start Chain Reaction
                </Button>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              {goalMet && !isCompleted && (
                <Button onClick={completeMission} className="bg-gradient-to-r from-emerald-500 to-teal-600" data-testid="complete-btn">
                  <Check className="w-4 h-4 mr-1" /> Goal Achieved!
                </Button>
              )}
              <Button variant="outline" onClick={resetState} className="border-white/20 text-muted-foreground" data-testid="reset-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Reset
              </Button>
            </div>

            <AnimatePresence>
              {showFact && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl border border-orange-500/20 bg-orange-500/5" data-testid="eng-fact">
                  <p className="text-xs text-orange-400 font-semibold flex items-center gap-1 mb-1"><Info className="w-3 h-3" /> Engineering Fact</p>
                  <p className="text-sm text-foreground/80">{mission.fact}</p>
                  {currentMission < MISSIONS.length - 1 && (
                    <Button size="sm" onClick={() => { setCurrentMission(p => p + 1); setShowMissions(false); resetState(); }}
                      className="mt-3 bg-orange-600 hover:bg-orange-500" data-testid="next-mission-btn">
                      Next Machine <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 lg:sticky lg:top-[65px] lg:self-start">
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><Cog className="w-4 h-4 text-orange-400" /> Objective</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{mission.description}</p>
            </div>
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2">Progress</h3>
              <div className="space-y-1.5">
                {MISSIONS.map((m, idx) => (
                  <button key={m.id} onClick={() => { if (unlockedMissions.includes(idx)) { setCurrentMission(idx); setShowMissions(false); resetState(); } }}
                    disabled={!unlockedMissions.includes(idx)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                      idx === currentMission ? 'bg-primary text-primary-foreground' :
                      completedMissions.includes(idx) ? 'text-emerald-400 hover:bg-muted/50' :
                      unlockedMissions.includes(idx) ? 'text-muted-foreground hover:bg-muted/50' : 'text-muted-foreground/30 cursor-not-allowed'
                    }`}>
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${
                      completedMissions.includes(idx) ? 'bg-emerald-500 text-white' :
                      idx === currentMission ? 'bg-primary text-primary-foreground' : 'bg-muted/50'
                    }`}>
                      {completedMissions.includes(idx) ? <Check className="w-3 h-3" /> : unlockedMissions.includes(idx) ? idx + 1 : <Lock className="w-2.5 h-2.5" />}
                    </span>
                    <span className="truncate">{m.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
